import { BUCK_CONVERTER, CONNECTOR } from "../../../../constants/componentType";
import auroraDBJson from "../../../Designs/auroraDbData";
import compDoNotStuff from "../../../helper/componentsHelper/compDoNotStuff";
import PowerTreeMaker from "../../../helper/componentsHelper/GetPath/getPowerTree_v2";
import designConstructor from "../../../helper/designConstructor";
import { newNanoId } from "../../../helper/idHelper";
import { getDefaultName } from "../../../helper/setDefaultName";
import compPinMap from "../../helper/compPinMap";
import componentSetting from "../../helper/compSettingHelper";
import componentTable from "../../helper/compTableHelper";
import { getAllCascadeComponents, getAllCascadeNets, getCascadeComponents, getPowerDomain, getPowerSwitchPinMap, getPreLayoutPinMap, getPreLayoutSetting } from "../../helper/setupData";
import preLayoutData from "../../prelayout/preLayoutData";
import { PowerTree, PowerTreeItem } from "./class";

async function generateTree({
  designId,
  pcbTable = [],
  connectors = [],
  useInput = false,
  powerTreeLevel = 8,
  setTreeMonitors
}) {
  let logs = [];
  const isPreLayout = designConstructor.isPreLayout(designId);
  try {
    if (isPreLayout && !preLayoutData.checkPreLayout(designId)) {
      await preLayoutData.getPreLayout(designId)
    } else if (!auroraDBJson.checkAuroraJson(designId)) {
      const setting = await componentSetting.getSetting({ designId });
      await auroraDBJson.getAuroraJson(designId, setting, { throwError: true })
    }
  } catch (error) {
    const designName = designConstructor.getDesignName(designId);
    logs.push([{ text: `\t${designName} acquisition failed, please check the network issue and refresh the Tree`, type: 'error' }])
    console.error("Get PCB Error:" + error);
  }
  const layoutComponents = getAllCascadeComponents({ pcbId: designId });
  const layoutNets = getAllCascadeNets({ pcbId: designId, info: true });
  const setting = isPreLayout ? getPreLayoutSetting(designId) : await componentSetting.getPrefixLib(designId);
  const table = await componentTable.getTableData(designId);
  const pinMap = await getPowerTreePinMapList(designId, pcbTable);
  const _pinConnection = await compPinMap.getPinConnection(designId);
  const pinConnection = await getPowerSwitchPinMap(_pinConnection);
  const doNotStuff = await compDoNotStuff.getDoNotStuff(designId);

  let filterTrees = [], powerTreeMakers = [];
  for (let row of pcbTable) {
    const { nets = [], partNumber, components: rootComps = [], type } = row;
    if (!nets.length) {
      continue;
    }
    let rootComponents = [...rootComps];
    if (!rootComponents.length) {
      rootComponents = [...layoutComponents.values()].filter(item => item.partName === partNumber).map(item => item.name);
    }
    let pinNames = nets.map(net => net.pinNames).flat(), broComponent = null;
    if (type === BUCK_CONVERTER) {
      const broComponents = partNumber.split(' - ');
      rootComponents = broComponents.filter(compName => pinNames.some(pin => pin.includes(compName)));
      rootComponents = rootComponents[0] ?  [rootComponents[0]] : [broComponents[0]];
      broComponent = broComponents.find(item => item !== rootComponents[0]);
      pinNames = pinNames.filter(pin => pin.includes(rootComponents[0])).map(pinName => pinName.replace(`${rootComponents[0]}_`, ''))
    }
    const pcbKey = connectors && connectors[0] && connectors[0][0] ? connectors[0][0].key : newNanoId(10, 'number');
    const connector = connectors.flat().find(item => item.key === pcbKey);
    for (let compName of rootComponents) {
      const component = layoutComponents.get(compName);
      if (!component) {
        continue;
      }
      const groundNets = getTreeComponentGnd(component, designId, pinMap, broComponent);
      const rootNets = [...new Set([...component.pins.values()].filter(pin => pinNames.includes(pin.pinName)).map(pin => pin.net))];
      for (let net of rootNets) {
        if (filterTrees.includes(`${net}-${compName}`)) {
          continue;
        }
        const powerTreeMaker = new PowerTreeMaker({
          // param
          compName,
          broComponent,
          powerNet: net,
          groundNets,
          designId,
          key: pcbKey,
          setting,
          table,
          pinMap,
          pinConnection,
          doNotStuff,
          layoutComponents,
          layoutNets,
          useInput,
          powerTreeLevel,
          connector,
          logs: [...logs, { text: `\tPMIC: ${compName} (${net})`, type: 'title' }],
          // fn
          getTreeComponentGnd
        })
        powerTreeMaker.generatePowerTrace();
        powerTreeMaker.generateGround();
        const traceData = powerTreeMaker.outputInfo();
        filterTrees = [...filterTrees, ...traceData.filterTree];
        setTreeMonitors(traceData.logs)
        powerTreeMakers.push({ traceData, name: traceData.name, key: traceData.key });
      }
    }
  }
  const _powerTreeMakers = powerTreeMakers.filter(maker => filterTrees.includes(maker.name));
  powerTreeMakers = _powerTreeMakers.length ? _powerTreeMakers : powerTreeMakers;
  let powerTrees = [];
  for (let maker of powerTreeMakers) {
    const { key, traceData } = maker;
    let connectTrees = [];
    await connectorTreeLoop({ key, traceData, connectors, connectTrees, useInput, powerTreeLevel, setTreeMonitors })
    powerTrees.push({ ...maker, connectTrees })
  }
  return powerTrees
}

async function connectorTreeLoop({ key, traceData, connectors, useInput, connectTrees, powerTreeLevel, setTreeMonitors }) {
  const connector = connectors.flat().find(item => item.key === key);
  if (connector) {
    const { paths } = traceData;
    const connectorCompItems = [...new Set(paths.flat().filter(comp => comp.type === CONNECTOR).map(comp => ({ name: comp.name, prevNet: comp.prevNet, gnds: comp.gnds })))];
    const connectorCompNames = connectorCompItems.reduce((acc, curr) => {
      const existing = acc.find(item => item.name === curr.name);
      if (existing) {
        existing.prevNets.push(curr.prevNet);
        existing.gnds.push(...curr.gnds)
      } else {
        acc.push({ name: curr.name, prevNets: [curr.prevNet], gnds: [...curr.gnds] });
      }
      return acc;
    }, []);

    const connectorComps = connector.components.filter(item => connectorCompNames.map(comp => comp.name).includes(item.name));
    const _pcbName = designConstructor.getDesignName(connector.pcb);
    for (let connectorComp of connectorComps) {
      const connectorNets = connectorCompNames.find(item => item.name === connectorComp.name);
      const { prevNets, gnds, name } = connectorNets
      for (let nextComp of (connectorComp.next || [])) {
        const { pcb, pcbKey, comp, nets } = nextComp;
        const rootNets = nets.filter(net => prevNets.includes(net[0])).map(net => net[1]).filter(net => !!net);
        const currentPrevNets = prevNets.filter(net => nets.some(n => n[0] === net));
        const groundNets = nets.filter(net => gnds.includes(net[0])).map(net => net[1]).filter(net => !!net);
        const pcbName = designConstructor.getDesignName(pcb);
        await traceConnectTree({
          connectors,
          useInput,
          designId: pcb,
          key: pcbKey,
          compName: comp,
          powerNets: rootNets,
          groundNets: groundNets,
          connectTrees,
          connectComp: { key, ...connectorNets },
          powerTreeLevel,
          setTreeMonitors,
          logs: [{ text: `\tConnection: (${_pcbName})[${currentPrevNets.join(', ')}] -> ${name} - ${comp} -> (${pcbName})[${rootNets.join(', ')}] `, type: 'title' }]
        })
      }
    }
  }
}

async function traceConnectTree({
  connectors = [],
  powerTreeLevel,
  useInput = false,
  designId,
  key,
  compName,
  powerNets,
  groundNets,
  connectTrees,
  connectComp,
  setTreeMonitors,
  logs
}) {
  const isPreLayout = designConstructor.isPreLayout(designId);
  try {
    if (isPreLayout && !preLayoutData.checkPreLayout(designId)) {
      await preLayoutData.getPreLayout(designId)
    } else if (!auroraDBJson.checkAuroraJson(designId)) {
      const setting = await componentSetting.getSetting({ designId });
      await auroraDBJson.getAuroraJson(designId, setting, { throwError: true })
    }
  } catch (error) {
    const designName = designConstructor.getDesignName(designId);
    logs.push([{ text: `\t${designName} acquisition failed, please check the network issue and refresh the Tree`, type: 'error' }])
    console.error("Get PCB Error:" + error);
  }
  const layoutComponents = getAllCascadeComponents({ pcbId: designId });
  const layoutNets = getAllCascadeNets({ pcbId: designId, info: true });
  const setting = isPreLayout ? getPreLayoutSetting(designId) : await componentSetting.getPrefixLib(designId);
  const table = await componentTable.getTableData(designId);
  const pinMap = await getPowerTreePinMapList(designId, []);
  const _pinConnection = await compPinMap.getPinConnection(designId);
  const pinConnection = await getPowerSwitchPinMap(_pinConnection);
  const doNotStuff = await compDoNotStuff.getDoNotStuff(designId);
  const connector = connectors.flat().find(item => item.key === key);

  for (let powerNet of powerNets) {
    const powerTreeMaker = new PowerTreeMaker({
      // param
      compName,
      powerNet: powerNet,
      groundNets,
      designId,
      key: key,
      setting,
      table,
      pinMap,
      pinConnection,
      doNotStuff,
      layoutComponents,
      layoutNets,
      useInput,
      powerTreeLevel,
      connector,
      isConnectTree: true,
      logs: [...logs, { text: `\tConnector: ${compName} (${powerNet})`, type: 'title' }],
      // fn
      getTreeComponentGnd
    });
    powerTreeMaker.generatePowerTrace();
    powerTreeMaker.generateGround();
    const traceData = powerTreeMaker.outputInfo();
    setTreeMonitors(traceData.logs)
    connectTrees.push({ traceData, name: traceData.name, key: traceData.key, prev: connectComp });
    await connectorTreeLoop({ key, traceData, connectors, connectTrees, useInput, powerTreeLevel, setTreeMonitors })
  }
}

async function tracePaths({
  powerTreeLevel,
  useInput = false,
  designId,
  key,
  compName,
  powerNets,
  groundNets,
  filterNets,
  edit = false
}) {
  const isPreLayout = designConstructor.isPreLayout(designId);
  let logs = [];
  try {
    if (isPreLayout && !preLayoutData.checkPreLayout(designId)) {
      await preLayoutData.getPreLayout(designId)
    } else if (!auroraDBJson.checkAuroraJson(designId)) {
      const setting = await componentSetting.getSetting({ designId });
      await auroraDBJson.getAuroraJson(designId, setting, { throwError: true })
    }
  } catch (error) {
    const designName = designConstructor.getDesignName(designId);
    logs.push([{ text: `\t${designName} acquisition failed, please check the network issue and refresh the Tree`, type: 'error' }])
    console.error("Get PCB Error:" + error);
  }
  const layoutComponents = getAllCascadeComponents({ pcbId: designId });
  const layoutNets = getAllCascadeNets({ pcbId: designId, info: true });
  const setting = isPreLayout ? getPreLayoutSetting(designId) : await componentSetting.getPrefixLib(designId);
  const table = await componentTable.getTableData(designId);
  const pinMap = await getPowerTreePinMapList(designId, []);
  const _pinConnection = await compPinMap.getPinConnection(designId);
  const pinConnection = await getPowerSwitchPinMap(_pinConnection);
  const doNotStuff = await compDoNotStuff.getDoNotStuff(designId);
  let paths = []

  for (let powerNet of powerNets) {
    const powerTreeMaker = new PowerTreeMaker({
      // param
      compName,
      powerNet: powerNet,
      groundNets,
      designId,
      key: key,
      setting,
      table,
      pinMap,
      pinConnection,
      doNotStuff,
      layoutComponents,
      layoutNets,
      useInput,
      powerTreeLevel,
      isConnectTree: true,
      filterNets,
      // fn
      getTreeComponentGnd
    });
    if (edit) {
      powerTreeMaker.generateEditTrace()
    } else {
      powerTreeMaker.generatePowerTrace();
      powerTreeMaker.generateGround();
    }
    const traceData = powerTreeMaker.outputInfo();
    paths.push(...traceData.paths, ...traceData.gndPaths)
  }

  // add only select component for edit
  if (powerNets && powerNets.length && edit) {
    const powerTreeMaker = new PowerTreeMaker({
      // param
      compName,
      powerNet: powerNets[0],
      groundNets,
      designId,
      key: key,
      setting,
      table,
      pinMap,
      pinConnection,
      doNotStuff,
      layoutComponents,
      layoutNets,
      useInput,
      powerTreeLevel,
      isConnectTree: true,
      filterNets,
      // fn
      getTreeComponentGnd
    });
    powerTreeMaker.generateRootTrace();
    const traceData = powerTreeMaker.outputInfo();
    paths.push(...traceData.paths, ...traceData.gndPaths)
  }
  return paths
}


async function getPowerTreePinMapList(designId, pcbTable) {
  const isPreLayout = designConstructor.isPreLayout(designId);
  const pinMap = isPreLayout ? getPreLayoutPinMap(designId) : await compPinMap.getPinMapLibraryData(designId);
  let tablePinMap = []
  for (let tableMap of pcbTable) {
    const { libraryId, libraryName, partNumber, components } = tableMap
    const data = await compPinMap.getSinglePinMapLibraryData(designId, tableMap);
    tablePinMap.push({ libraryId, libraryName, partNumber, data, components })
  }
  return [...pinMap, ...tablePinMap]
}

function getTreeComponentGnd(component, designId, pinMap, broComponent, ground, filterNets = []) {
  const { name, partName, pins } = component;
  let pmicPinMap = pinMap.find(map => map.partNumber === partName && map.components && map.components.includes(name));
  if (!pmicPinMap) {
    pmicPinMap = pinMap.find(map => map.partNumber === partName);
  }

  if (broComponent) {
    pmicPinMap = pinMap.find(map => [`${name} - ${broComponent}`, `${broComponent} - ${name}`].includes(map.partNumber) && map.type === BUCK_CONVERTER)
  }
  if (pmicPinMap && pmicPinMap.data) {
    const groundPins = pmicPinMap.data.map(row => row.ground).flat();
    if (pmicPinMap.type === BUCK_CONVERTER) {
      if (groundPins.length) {
        if (groundPins.some(pin => pin.includes(name))) {
          const groundNets = [...pins.values()].filter(pin => groundPins.includes(`${name}_${pin.pinName}`)).map(pin => pin.net);
          return [...new Set(groundNets)];
        } else {
          const broCompInfo = getCascadeComponents({ pcbId: designId, name: broComponent });
          if (broCompInfo) {
            const groundNets = [...broCompInfo.pins.values()].filter(pin => groundPins.includes(`${broComponent}_${pin.pinName}`)).map(pin => pin.net);
            return [...new Set(groundNets)];
          }
        }
      }
    } else {
      if (groundPins.length) {
        const groundNets = [...pins.values()].filter(pin => groundPins.includes(pin.pinName)).map(pin => pin.net);
        return [...new Set(groundNets)];
      }
    }
  }
  if (ground && [...pins.values()].some(pin => pin.net === ground)) {
    return [ground];
  }
  const groundNet = getPowerDomain({ chip: name, designId, prevGround: ground, filterNets }).groundNet;
  return [groundNet]
}

function getPowerTreeClass(powerTrees, connectors, treeColor) {
  let _powerTrees = [...powerTrees];
  const hasTrees = powerTrees.some(t => t.tree && t.tree.length > 1);
  if (hasTrees) {
    _powerTrees = _powerTrees.filter(t => t.tree && t.tree.length > 1)
  }
  return _powerTrees.map(powerTree => {
    const { logs, name, paths, root, tree, key } = powerTree;
    const deep = Math.max(...tree.flat().map(item => item.deep + ((item.number || 1) - 1)));
    return new PowerTree({
      name,
      tree: {
        key: name,
        root,
        branch: tree.map(branch => branch.map(item => new PowerTreeItem(item))),
        name,
        deep,
        pcbIndex: getPCBIndex(key, connectors, treeColor),
        logs,
        paths,
        mainPCB: key
      }
    })
  })
}

function getPCBIndex(mainPCB, connectors, treeColor) {
  let pcbNames = [];
  const randomPCBIndex = connectors.flat().map(item => {
    const { key, pcb } = item;
    const treeIndex = treeColor.find(item => item.key === key);
    const pcbName = designConstructor.getDesignName(pcb);
    const newPCBName = pcbNames.includes(pcbName) ? getDefaultName({ nameList: pcbNames, defaultKey: pcbName, delimiter: " (", endDelimiter: ")", firstIndex: 1, key: "" }) : pcbName;
    pcbNames.push(newPCBName)
    return {
      key,
      pcbId: pcb,
      pcbName: newPCBName,
      color: mainPCB === key ? "#FFFFFF" : treeIndex && treeIndex.color ? treeIndex.color : randomColor()
    }
  })
  return [...randomPCBIndex].sort((a) => mainPCB === a.key ? -1 : 1);
}

function randomColor() {
  let color = ['#', 'E', 'F', 'E', 'F', 'E', 'F'];
  let limit = ['F', 'E', 'D']
  for (let i = 2; i < 7; i = i + 2) {
    color[i] = parseInt(Math.random() * 16).toString(16);
  }

  for (let i = 1; i < 7; i = i + 2) {
    color[i] = limit[parseInt(Math.random() * 3).toString(16)];
  }
  return color.join('');
}

async function refershTree({
  designId,
  pcbTable = [],
  connectors = [],
  useInput = false,
  powerTreeLevel = 8,
  setTreeMonitors,
  compName,
  powerNet
}) {
  const isPreLayout = designConstructor.isPreLayout(designId);
  let logs = [];
  try {
    if (isPreLayout && !preLayoutData.checkPreLayout(designId)) {
      await preLayoutData.getPreLayout(designId)
    } else if (!auroraDBJson.checkAuroraJson(designId)) {
      const setting = await componentSetting.getSetting({ designId });
      await auroraDBJson.getAuroraJson(designId, setting, { throwError: true })
    }
  } catch (error) {
    const designName = designConstructor.getDesignName(designId);
    logs.push([{ text: `\t${designName} acquisition failed, please check the network issue and refresh the Tree`, type: 'error' }])
    console.error("Get PCB Error:" + error);
  }
  const layoutComponents = getAllCascadeComponents({ pcbId: designId });
  const layoutNets = getAllCascadeNets({ pcbId: designId, info: true });
  const setting = isPreLayout ? getPreLayoutSetting(designId) : await componentSetting.getPrefixLib(designId);
  const table = await componentTable.getTableData(designId);
  const pinMap = await getPowerTreePinMapList(designId, pcbTable);
  const _pinConnection = await compPinMap.getPinConnection(designId);
  const pinConnection = await getPowerSwitchPinMap(_pinConnection);
  const doNotStuff = await compDoNotStuff.getDoNotStuff(designId);

  let filterTrees = [], powerTreeMakers = [];
  for (let row of pcbTable) {
    const { nets = [], partNumber, components: rootComps = [], type } = row;
    if (!nets.length) {
      continue;
    }
    let rootComponents = [...rootComps];
    if (!rootComponents.length) {
      rootComponents = [...layoutComponents.values()].filter(item => item.partName === partNumber).map(item => item.name);
    }
    let pinNames = nets.map(net => net.pinNames).flat(), broComponent = null;
    if (type === BUCK_CONVERTER) {
      const broComponents = partNumber.split(' - ');
      rootComponents = broComponents.filter(compName => pinNames.some(pin => pin.includes(compName)));
      rootComponents = rootComponents[0] ?  [rootComponents[0]] : [broComponents[0]];
      broComponent = broComponents.find(item => item !== rootComponents[0]);
      pinNames = pinNames.filter(pin => pin.includes(rootComponents[0])).map(pinName => pinName.replace(`${rootComponents[0]}_`, ''))
    }
    const pcbKey = connectors && connectors[0] && connectors[0][0] ? connectors[0][0].key : newNanoId(10, 'number');
    const connector = connectors.flat().find(item => item.key === pcbKey);
    const component = getCascadeComponents({ pcbId: designId, name: compName })
    const groundNets = getTreeComponentGnd(component, designId, pinMap, broComponent);
    const powerTreeMaker = new PowerTreeMaker({
      // param
      compName,
      broComponent,
      powerNet,
      groundNets,
      designId,
      key: pcbKey,
      setting,
      table,
      pinMap,
      pinConnection,
      doNotStuff,
      layoutComponents,
      layoutNets,
      useInput,
      powerTreeLevel,
      connector,
      logs: [...logs, { text: `\tPMIC: ${compName} (${powerNet})`, type: 'title' }],
      // fn
      getTreeComponentGnd
    })
    powerTreeMaker.generatePowerTrace();
    powerTreeMaker.generateGround();
    const traceData = powerTreeMaker.outputInfo();
    filterTrees = [...filterTrees, ...traceData.filterTree];
    setTreeMonitors(traceData.logs)
    powerTreeMakers.push({ traceData, name: traceData.name, key: traceData.key });
  }
  const _powerTreeMakers = powerTreeMakers.filter(maker => filterTrees.includes(maker.name));
  powerTreeMakers = _powerTreeMakers.length ? _powerTreeMakers : powerTreeMakers;
  let powerTrees = [];
  for (let maker of powerTreeMakers) {
    const { key, traceData } = maker;
    let connectTrees = [];
    await connectorTreeLoop({ key, traceData, connectors, connectTrees, useInput, powerTreeLevel, setTreeMonitors })
    powerTrees.push({ ...maker, connectTrees })
  }
  return powerTrees
}

export {
  generateTree,
  getPowerTreeClass,
  tracePaths,
  refershTree,
  getPowerTreePinMapList,
  getTreeComponentGnd
}